import React from 'react';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from 'sensortower-components/src/utils/styled';
import { useTheme } from '@mui/material/styles';
import { CollectionProps } from 'sensortower-components/src/base-components/Collection';
import { CardProps } from 'sensortower-components/src/base-components/Card';
import { TextProps } from 'sensortower-components/src/base-components/Text';
import ContentModule from 'sensortower-components/src/base-components/ContentModule';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import { range } from 'lodash-es';

import Filters from '../Filters';
import Pagination from '../Pagination';
import { Option } from '../Filter';
import { useLocalizationContext } from '../LocalizationContext';
import { sidekick } from '../../utils/sidekick';
import getGridImageSizes from '../../utils/base/getGridImageSizes';
import CollectionFilteredIntroText from './CollectionFilteredIntroText';
import { generateFilterYearOptions, getNoResultsMessage } from './utils/filter';
import { useCollectionFilteredItems } from './hooks/useCollectionFilteredItems';
import { DEFAULT_ORDER } from './consts';

interface FilterOptions {
  [key: string]: Option[];
}

interface SettingsFilter {
  id: string;
  label: string;
  multiple: boolean;
  hidden?: boolean;
  includedYears?: Array<string | number>;
}

export interface Settings {
  filters: SettingsFilter[];
  contentType?: string;
}

export interface CollectionFilteredProps extends Omit<CollectionProps, 'id' | '__typename'> {
  __typename?: string;
  id?: string;
  introText?: TextProps;
  itemsVariant?: string;
  itemsConnection?: { items?: CardProps[]; total?: number };
  filterOptions?: FilterOptions;
  numberOfItems?: number;
  settings?: Settings;
  sidekickLookup?: any;
}

export interface Query {
  [key: string]: string | string[] | undefined;
}

// Grid columns each card spans: one per row on phones, two on tablets, three on desktop.
const CARD_GRID_SIZE = { xs: 12, md: 6, lg: 4 };
// Cards whose image is in the viewport on load at every width, so it loads
// eagerly (with a preload) instead of being a lazy-loaded LCP candidate. Only
// the first: on phones the grid is one card per row, and preloading the rest
// of the desktop row would compete with that LCP image for bandwidth. Wider
// layouts still fetch their in-viewport images promptly (native lazy loading
// starts them as soon as layout is known).
const EAGER_CARD_COUNT = 1;

const localizedLabel = (localization: ReturnType<typeof useLocalizationContext>, key: string, fallback: string) =>
  localization[key]?.shortTextValue ?? fallback;

export const CollectionFiltered = ({
  sidekickLookup,
  filterOptions,
  numberOfItems,
  itemsConnection,
  settings,
  ...props
}: CollectionFilteredProps) => {
  const localization = useLocalizationContext();
  const theme = useTheme();
  const cardMediaSizes = getGridImageSizes(CARD_GRID_SIZE, theme.breakpoints.values);

  const {
    data,
    query,
    limit,
    page,
    pageKey,
    pages,
    isPress,
    isLoadingItems,
    hasActiveFilter,
    hasNoResults,
    handleClearSearch,
  } = useCollectionFilteredItems({ id: props.id, numberOfItems, settings, itemsConnection });

  const allFilters = settings?.filters?.filter((f) => f.id !== 'order') ?? [];
  const visibleFilters = allFilters.filter((f) => !f.hidden);
  const orderFilter = settings?.filters?.find((o) => o.id === 'order');

  const resolvedFilters = allFilters.map((o) => ({
    ...o,
    label: localizedLabel(localization, `common.filter${o.label}Label`, o.label),
    variant: 'outlined' as const,
    items: o.id === 'year' ? generateFilterYearOptions(o.includedYears) : (filterOptions?.[o.id] ?? []),
  }));

  return (
    <ErrorBoundary>
      <Root
        maxWidth="xl"
        id="search"
        data-testid="CollectionFiltered"
        {...sidekick(sidekickLookup)}
        sx={props.styles?.root}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: { xs: 'column', md: isPress ? 'row' : 'column' },
            gap: { xs: 0, md: isPress ? 12 : 0 },
          }}
        >
          {props.introText && (
            <Box sx={{ flexShrink: 0 }}>
              <CollectionFilteredIntroText
                introText={props.introText}
                filter={query}
                localization={localization}
                onClearSearch={handleClearSearch}
              />
            </Box>
          )}

          {visibleFilters.length > 0 ? (
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Filters
                pageKey={pageKey}
                filters={resolvedFilters}
                sort={
                  orderFilter?.hidden
                    ? null
                    : {
                        id: 'order',
                        variant: 'outlined',
                        label: orderFilter?.label || 'Sort',
                        defaultSelection: [DEFAULT_ORDER],
                        items: [
                          {
                            value: 'fields.pubDate',
                            label: localizedLabel(localization, 'common.dateAscendingLabel', 'Date Ascending'),
                          },
                          {
                            value: '-fields.pubDate',
                            label: localizedLabel(localization, 'common.dateDescendingLabel', 'Date Descending'),
                          },
                        ],
                      }
                }
              />
            </Box>
          ) : null}
        </Box>

        <Grid container spacing={4}>
          <Grid container size={{ xs: 12 }} spacing={4} aria-busy={isLoadingItems || undefined}>
            {hasActiveFilter && hasNoResults && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
                  {getNoResultsMessage(localization)}
                </Typography>
              </Grid>
            )}

            {!isLoadingItems &&
              (data?.items as CardProps[] | undefined)?.map((card, index: number) => {
                const isLast = index + 1 >= (data?.items?.length ?? 0);
                return (
                  <Grid key={card?.id} size={CARD_GRID_SIZE}>
                    <ContentModule
                      {...card}
                      variant={props.itemsVariant ?? (card as CardProps)?.variant}
                      mediaSizes={cardMediaSizes}
                      // Press cards render a small logo or (press releases) no media at all,
                      // so there is nothing there worth an eager load.
                      mediaPriority={index < EAGER_CARD_COUNT && !isPress}
                    />
                    {!isPress && !isLast && <MobileDivider />}
                  </Grid>
                );
              })}

            {isLoadingItems &&
              range(limit).map((_, index) => (
                <Grid key={`loading-card-${index}`} size={CARD_GRID_SIZE}>
                  <ContentModule __typename={'Card'} variant={props.itemsVariant} loading />
                  {!isPress && index + 1 < limit && <MobileDivider />}
                </Grid>
              ))}
          </Grid>

          {/* Pagination on small screen */}
          <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', md: 'none' } }}>
            <Box sx={{ my: 2 }}>
              <Pagination count={pages} page={page} size="small" pageKey={pageKey} />
            </Box>
          </Grid>
        </Grid>

        {/* Pagination on larger screens */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 8, mb: 2 }}>
          <Pagination count={pages} page={page} pageKey={pageKey} />
        </Box>
      </Root>
    </ErrorBoundary>
  );
};

const MobileDivider = styled(Divider, {
  name: 'CollectionFiltered',
  slot: 'MobileDivider',
})(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(4),
  marginBottom: 0,
  width: '100%',
  borderColor: theme.palette.primary.main,
  [theme.breakpoints.up('sm')]: {
    width: '95%',
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const Root = styled(Container, {
  name: 'CollectionFiltered',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'variant',
  overridesResolver: (_, styles) => ({
    ...(styles.root as any),
  }),
})(({ theme }) => ({
  paddingTop: theme.spacing(4),
  [theme.breakpoints.up('lg')]: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
  },
  '& h2': {
    '&.MuiTypography-root': {
      color: theme.palette.secondary.contrastText,
    },
  },
  '& [class*="MuiGrid-container"] [class*="MuiGrid-container"]': {
    '& [class*="Card-root"]': {
      [theme.breakpoints.down('md')]: {
        '& h3': {
          marginTop: theme.spacing(2),
        },
      },
    },
  },
  '& h3': {
    marginBottom: theme.spacing(1),
  },
  '& [class*="MuiGrid-container"] [class*="MuiDivider-root"]': {
    width: 'calc(100% - 16px)',
  },
  '& [class*="MuiPaginationItem-root"]': {
    fontWeight: 200,
    '&[class*="Mui-selected"]': {
      borderRadius: 10,
    },
  },
  '& [class*="MuiPaginationItem-previousNext"]': {
    fontSize: 19,
    fontWeight: 600,
    '& svg': {
      fontSize: 25,
    },
  },
}));

export default CollectionFiltered;
